import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { FaInstagram, FaFacebook, FaCheck, FaSpinner, FaTimes, FaArrowLeft } from "react-icons/fa";
import { InstagramAccountCard } from "./InstagramAccountCard";
import { FacebookAccountCard } from "./FacebookAccountCard";
import { InstagramAccount, InstagramConnectionState, InstagramSelectionState, InstagramSelectionAccount } from "../types";
import { MAX_INSTAGRAM_ACCOUNTS_VIA_FACEBOOK } from "@/config";
import { PrimaryActionButton } from "@/components/ui/PrimaryActionButton";
import { GlassPanel } from "@/components/ui/GlassPanel";

interface InstagramSectionProps {
  isLoadingInstagram: boolean;
  isConnecting: boolean;
  connection: InstagramConnectionState;
  isIndexing: Record<string, boolean>;
  currentMessageIndex: Record<string, number>;
  indexingMessages: string[];
  onConnectViaFacebook: () => void;
  onConnectDirect: () => void;
  onDisconnect: (connectionId?: string) => void;
  onTriggerIndex: (accountId: string, connectionId: string) => void;
  instagramSelectionState: InstagramSelectionState;
  onFetchAvailableAccounts: () => Promise<{ success: boolean; accounts?: InstagramSelectionAccount[]; error?: string }>;
  onSelectAccount: (accountId: string, facebookConnectionId: string) => Promise<{ success: boolean; error?: string }>;
  onReopenSelection: () => Promise<void>;
  onCloseSelection?: () => void;
  variant?: "page" | "popup";
  onBack?: () => void;
  onConnectMore?: () => void;
}

export function InstagramSection({
  isLoadingInstagram,
  isConnecting,
  connection,
  isIndexing,
  currentMessageIndex,
  indexingMessages,
  onConnectViaFacebook,
  onConnectDirect,
  onDisconnect,
  onTriggerIndex,
  instagramSelectionState,
  onFetchAvailableAccounts,
  onSelectAccount,
  onReopenSelection,
  onCloseSelection,
  variant = "page",
  onBack,
  onConnectMore,
}: InstagramSectionProps) {
  const { t } = useTranslation();
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const router = useRouter();
  const sectionClassName = variant === "popup" ? "text-white space-y-6 w-full" : "no-border rounded-lg shadow-sm";
  const connectMoreTranslation = t('account.connect_more_accounts');
  const connectMoreText = connectMoreTranslation && connectMoreTranslation !== 'account.connect_more_accounts'
    ? connectMoreTranslation
    : 'Connect more accounts';

  // Reusable component for no connection state
  const NoConnectionState = () => (
    <GlassPanel className="text-center p-4 md:p-6 space-y-6 ">
      <FaInstagram className="mx-auto h-12 w-12" />
      <h3 className="text-lg font-medium">{t('account.no_accounts_found')}</h3>

      <div className="space-y-3 max-w-sm mx-auto mt-6">
        {/* Facebook connection button - Top priority */}
        <Button
          variant="ghost"
          size="sm"
          className="w-full flex items-center justify-center gap-2 bg-white border-0 text-black hover:bg-white/90"
          onClick={onConnectViaFacebook}
          disabled={isConnecting}
        >
          <FaFacebook className="h-4 w-4" style={{ fill: '#1877F2', color: '#1877F2' }} />
          {isConnecting ? t('account.connecting') : t('account.connect_with_facebook')}
        </Button>

        {/* Direct Instagram connection button - Secondary */}
        <button
          type="button"
          onClick={onConnectDirect}
          disabled={isConnecting}
          className="w-full flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-0 border-0"
        >
          <FaInstagram className="h-4 w-4" style={{ color: '#E1306C', fill: '#E1306C' }} />
          {isConnecting ? t('account.connecting') : t('account.connect_with_instagram')}
        </button>
      </div>

      
    </GlassPanel>
  );

  // Handler for going back to account page from Instagram selection
  const handleGoBack = () => {
    if (variant === "popup" && onBack) {
      onBack();
    }
    // Prefer closing selection state in-place to avoid navigation flashes
    if (onCloseSelection) {
      onCloseSelection();
      if (variant === "popup" && onBack) {
        return;
      }
    }
    // Fallback: client-side navigation
    router.push('/account');
  };

  // Fetch available accounts when selection is needed
  // Auto-select first account when accounts are loaded
  useEffect(() => {
    if (
      instagramSelectionState.availableAccounts.length > 0 && 
      selectedAccountIds.length === 0
    ) {
      const firstAccount = instagramSelectionState.availableAccounts[0];
      if (firstAccount) {
        setSelectedAccountIds([firstAccount.id]);
      }
    }
  }, [instagramSelectionState.availableAccounts, selectedAccountIds.length]);

  // Fetch available accounts when needed
  useEffect(() => {
    if (instagramSelectionState.needsSelection && !instagramSelectionState.hasTriedFetching) {
      onFetchAvailableAccounts();
    }
  }, [instagramSelectionState.needsSelection, instagramSelectionState.hasTriedFetching, onFetchAvailableAccounts]);

  const handleAccountSelection = (accountId: string) => {
    setSelectedAccountIds(prev => {
      if (prev.includes(accountId)) {
        // Remove if already selected
        return prev.filter(id => id !== accountId);
      } else {
        // Add if not selected, but respect the limit
        if (prev.length < MAX_INSTAGRAM_ACCOUNTS_VIA_FACEBOOK) {
          return [...prev, accountId];
        } else {
          // Replace the first selection if at limit
          return [accountId];
        }
      }
    });
  };

  const handleSelectAccounts = async () => {
    if (selectedAccountIds.length === 0) return;
    
    // For now, just select the first account (N=1)
    const selectedAccount = instagramSelectionState.availableAccounts.find(acc => acc.id === selectedAccountIds[0]);
    if (!selectedAccount) return;

    await onSelectAccount(selectedAccountIds[0], selectedAccount.facebook_connection_id);
  };

  const handleRetryFetch = async () => {
    await onFetchAvailableAccounts();
  };

  if (isLoadingInstagram) {
    return (
      <div className={sectionClassName + " text-white"}>
        <div className="flex items-center justify-between mb-4">
          {variant === "popup" ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-white"
                onClick={onBack}
                disabled={!onBack}
              >
                <FaArrowLeft />
                <span className="sr-only">Back</span>
              </Button>
              <h3 className="text-2xl font-normal">Instagram</h3>
              <div className="h-8 w-8" />
            </>
          ) : (
            <h3 className="text-2xl font-normal mb-4">Instagram</h3>
          )}
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-neutral-800 rounded w-3/4 mb-4"></div>
          <div className="h-8 bg-neutral-800 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  const isConnected = connection.currentConnectionType !== null;
  const hasAccounts = connection.accounts.length > 0;

  // Show Instagram account selection if needed
  if (instagramSelectionState.needsSelection) {
    return (
      <GlassPanel className="text-center p-4 md:p-6 space-y-6 ">
        <div className="text-center mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button
              onClick={handleGoBack}
              variant="ghost"
              size="icon"
              className="text-white hover:text-white hover:bg-transparent w-8 h-8"
            >
              <FaArrowLeft />
              <span className="sr-only">Go Back to Account Page</span>
            </Button>
            <div></div> {/* Spacer for centering */}
          </div>
          

          <h3 className="text-2xl  mb-2">
            {t('account.select_account_title')}
          </h3>
          <p>
            {t('account.select_account_description')}
          </p>
        </div>

        {instagramSelectionState.isLoadingAccounts ? (
          <div className="text-center py-8">
            <FaSpinner className="text-4xl animate-spin mx-auto mb-4" />
          </div>
        ) : instagramSelectionState.availableAccounts.length === 0 ? (
          <div className="text-center py-8">
            <FaInstagram className="text-4xl mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {t('account.no_professional_accounts_found')}
            </h3>
            <p>
              {t('account.no_professional_accounts_description')}
            </p>
            
            <div className="flex flex-col items-center space-y-3">
              <PrimaryActionButton 
                onClick={handleRetryFetch}
                disabled={instagramSelectionState.isLoadingAccounts}
                size="sm"
                className="flex items-center gap-2"
              >
                {instagramSelectionState.isLoadingAccounts ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    {t('account.retrying')}
                  </>
                ) : (
                  <>
                    <FaInstagram />
                    {t('account.try_again')}
                  </>
                )}
              </PrimaryActionButton>
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-w-2xl mx-auto">
            <div className="space-y-3">
              {instagramSelectionState.availableAccounts.map((account) => (
                <div
                  key={account.id}
                  className={`group rounded-lg p-4 cursor-pointer transition-colors bg-white/5 border border-white/10 hover:bg-white/20 text-white`}
                  onClick={() => handleAccountSelection(account.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mr-16">
                        {account.profile_picture_url ? (
                          <img
                            src={account.profile_picture_url}
                            alt={account.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FaInstagram className="text-white text-lg" />
                        )}
                      </div>
                      <div>
                        <h4 className=" text-white">{account.name}</h4>
                        <p className="text-sm text-neutral-300">@{account.username}</p>
                      </div>
                    </div>
                    
                    {selectedAccountIds.includes(account.id) && (
                      <FaCheck className="text-white" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center mt-6">
              <PrimaryActionButton
                onClick={handleSelectAccounts}
                disabled={selectedAccountIds.length === 0}
                isLoading={instagramSelectionState.isSelecting}
                loadingText={t('account.processing')}
                size="lg"
                className=""
              >
                {t('account.continue')}
              </PrimaryActionButton>
            </div>
          </div>
        )}
      </GlassPanel>
    );
  }

  return (
    <div className={sectionClassName}>
      <div className="flex items-center justify-between mb-4">
        {variant === "popup" ? (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-white"
              onClick={onBack}
              disabled={!onBack}
            >
              <FaArrowLeft />
              <span className="sr-only">Back</span>
            </Button>
            <h3 className="text-2xl font-normal text-white">Instagram</h3>
            <div className="h-8 w-8" />
          </>
        ) : (
          <h3 className="text-2xl font-normal mb-4 text-white">Instagram</h3>
        )}
      </div>

      {/*
      <p className="mb-6">
        {isConnected 
            ? `Your connected Instagram Professional Accounts via ${connection.currentConnectionType === 'instagram_direct' ? 'Direct Instagram' : 'Facebook Login'}`
          : "Your connected Instagram Professional Accounts"
        }
      </p>
      */}

      {isConnected ? (
        <>
          {hasAccounts ? (
            <div className="space-y-4">
              {connection.accounts.map((account) => (
                <InstagramAccountCard
                  key={account.id}
                  account={account}
                  isLoadingInstagram={isLoadingInstagram}
                  isIndexing={isIndexing}
                  currentMessageIndex={currentMessageIndex}
                  indexingMessages={indexingMessages}
                  onDisconnect={onDisconnect}
                  onTriggerIndex={onTriggerIndex}
                  onReopenSelection={connection.currentConnectionType === 'instagram_via_facebook' ? onReopenSelection : undefined}
                />
              ))}
            </div>
          ) : connection.currentConnectionType === 'instagram_via_facebook' ? (
            <div className="space-y-4">
              {connection.facebookProfile && (
                <FacebookAccountCard
                  profile={{
                    id: connection.facebookProfile.connectionId || '',
                    name: connection.facebookProfile.name || 'Facebook User',
                    email: connection.facebookProfile.username,
                    picture_url: connection.facebookProfile.picture_url,
                    connected_at: connection.facebookProfile.connected_at || new Date().toISOString(),
                    connectionId: connection.facebookProfile.connectionId || ''
                  }}
                  isLoading={isLoadingInstagram}
                  onDisconnect={onDisconnect}
                  onFetchInstagramAccounts={onReopenSelection}
                  isFetchingInstagramAccounts={isConnecting}
                />
              )}
            </div>
          ) : (
            <NoConnectionState />
          )}
        </>
      ) : (
        <NoConnectionState />
      )}
      {variant === "popup" && onConnectMore && (
        <Button
          type="button"
          variant="ghost"
          className="w-full border border-white/10 bg-white/5 text-white hover:bg-white/10"
          onClick={onConnectMore}
        >
          {connectMoreText}
        </Button>
      )}
    </div>
  );
} 